"use client";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui";
import { StarRating } from "./StarRating";
import type { PublicReview } from "@/api/reviewApi";

type PublicReviewCardProps = {
  review: PublicReview;
};

/**
 * One card in a public reviews list (item detail "Reviews" section).
 * Renders only the PII-safe `PublicReview` shape — reviewerName, rating,
 * freeText, and an optional business reply. Never receives customerId,
 * internalNote, or orderId (the BFF/backend already strip those).
 */
export function PublicReviewCard({ review }: PublicReviewCardProps) {
  const { t, i18n } = useTranslation();
  const dateLabel = React.useMemo(() => {
    if (!review.createdAt) return null;
    const d = new Date(review.createdAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(i18n.language, { month: "short", day: "numeric", year: "numeric" });
  }, [review.createdAt, i18n.language]);

  return (
    <Card padding={14} style={{ textAlign: "start" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink-1)" }}>
          {review.reviewerName}
        </div>
        {dateLabel && (
          <div style={{ fontSize: 11.5, color: "var(--ink-3)", flexShrink: 0 }}>{dateLabel}</div>
        )}
      </div>
      <StarRating value={review.rating} readOnly size={14} justify="flex-start" />
      {review.freeText && (
        <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, marginTop: 8 }}>
          {review.freeText}
        </div>
      )}
      {review.operatorReply?.text && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: "var(--surface-2)",
            borderInlineStart: "3px solid var(--primary-500)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--primary-700)",
              textTransform: "uppercase",
              letterSpacing: 0.2,
              marginBottom: 4,
            }}
          >
            {t("v2.reviews.public.operator_reply_label", "Response from the restaurant")}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
            {review.operatorReply.text}
          </div>
        </div>
      )}
    </Card>
  );
}
